import {
  PiWarningDuotone as AlertTriangle,
  PiBarcodeDuotone as Barcode,
  PiChartBarDuotone as ChartBar,
  PiClipboardTextDuotone as ClipboardList,
  PiClockCounterClockwiseDuotone as History,
  PiCopyDuotone as Copy,
  PiCreditCardDuotone as CreditCard,
  PiEnvelopeSimpleDuotone as Envelope,
  PiFileMinusDuotone as FileMinus,
  PiFileTextDuotone as FileText,
  PiFileXlsDuotone as FileSpreadsheet,
  PiGearSixDuotone as Gear,
  PiGaugeDuotone as Gauge,
  PiHandshakeDuotone as Handshake,
  PiHashDuotone as Hash,
  PiHourglassDuotone as Hourglass,
  PiImagesDuotone as Images,
  PiMegaphoneDuotone as Megaphone,
  PiKeyDuotone as Key,
  PiDeviceMobileDuotone as DeviceMobile,
  PiStackDuotone as Layers,
  PiSquaresFourDuotone as LayoutDashboard,
  PiListChecksDuotone as ListChecks,
  PiMapPinDuotone as MapPin,
  PiPackageDuotone as Package,
  PiPlugDuotone as Plug,
  PiPlusBold as Plus,
  PiReceiptDuotone as Receipt,
  PiScalesDuotone as Scales,
  PiScrollDuotone as ScrollText,
  PiSlidersHorizontalDuotone as Sliders,
  PiTagDuotone as Tags,
  PiTreeStructureDuotone as TreeStructure,
  PiTruckDuotone as Truck,
  PiArrowCounterClockwiseDuotone as Undo2,
  PiUserGearDuotone as UserGear,
  PiUserPlusDuotone as UserPlus,
  PiUserSwitchDuotone as UserSwitch,
  PiUsersDuotone as Users,
  PiWalletDuotone as Wallet,
  PiWarningOctagonDuotone as WarningOctagon,
} from "react-icons/pi";
import type { MenuItem } from "@steelyard/theme";
import type { NavEntry, NavGroup, Permission } from "../types";

/**
 * Ported verbatim from apps/admin/lib/nav.ts — business config, not UI, unchanged by the stack
 * migration. Absence still means "not permitted": `visibleNav` only offers built screens the
 * caller can reach, `pendingNav` surfaces the rest (permitted but unbuilt) with a reason, and
 * nothing is silently dropped.
 */

export const NAV_GROUPS: readonly NavGroup[] = [
  "Overview",
  "Catalogue",
  "Pricing",
  "Orders",
  "Inventory",
  "Customers",
  "Invoicing",
  "Finance",
  "Data",
  "Integrations",
  "Compliance",
  "Packing",
  "Administration",
  "Website",
  "Content",
];

export const NAV: readonly NavEntry[] = [
  { href: "/", label: "Dashboard", group: "Overview", icon: LayoutDashboard, built: true },
  // 008-reporting-dashboards T025: the index for every report below — no `permission` (every
  // signed-in account can reach it; it filters its own rows by the same permission each report's
  // own entry already requires, same as nav.tsx's own "no permission" convention above).
  { href: "/reports", label: "Reports", group: "Overview", icon: FileText, built: true },
  { href: "/catalogue/brands", label: "Brands", permission: "catalogue.read", group: "Catalogue", icon: Tags, built: true },
  { href: "/catalogue/categories", label: "Categories", permission: "catalogue.read", group: "Catalogue", icon: TreeStructure, built: true },
  { href: "/catalogue/products", label: "Products", permission: "catalogue.read", group: "Catalogue", icon: Package, built: true },
  // T103: GAP-1 closed — GET /skus exists, so this replaces the T053 create-only "New SKU" entry.
  // "New SKU" is reached from this list's own button now, same as Brands/Categories/Products above.
  { href: "/catalogue/skus", label: "SKUs", permission: "catalogue.read", group: "Catalogue", icon: Barcode, built: true },
  // BUG-08: the GstRate registry the SKU form's dropdown reads from.
  { href: "/catalogue/gst-rates", label: "GST rates", permission: "catalogue.read", group: "Catalogue", icon: Tags, built: true },
  // T059: tier list built; flipped from built:false now that /tiers has a screen.
  // "New tier" is reached from this list's own button now, same as Brands/Categories/Products/SKUs above.
  // pricing.read, not pricing.tiers.readAny: every tier screen past the list reads GET /tiers/{id}
  // (pricing.read). readAny only feeds the terms form's tier list (docs/rbac-matrix.md), so Accounts
  // gets no Tiers entry rather than a list whose every row 403s.
  { href: "/tiers", label: "Tiers", permission: "pricing.read", group: "Pricing", icon: Layers, built: true },
  // 008-reporting-dashboards T021: this named an unbuilt placeholder screen at "/staleness"; that
  // screen is `reports/staleness` now that it exists (Staleness.tsx), so the entry points there
  // instead of a second, duplicate nav row being added beside it.
  { href: "/reports/staleness", label: "Stale prices", permission: "pricing.read", group: "Pricing", icon: Hourglass, built: true },
  // T085-T090: importer wizard + diff screen built; flipped from built:false now that /imports has a screen.
  // `exact`: the uploader is catalogue.administer, but a batch's diff (/imports/:batchNumber, linked from
  // Import history) is catalogue.read — Diff.tsx gates it and its commit button itself.
  { href: "/imports", label: "Imports", permission: "catalogue.administer", group: "Data", icon: FileSpreadsheet, built: true, exact: true },
  { href: "/imports/history", label: "Import history", permission: "catalogue.read", group: "Data", icon: History, built: true },
  { href: "/orders", label: "Orders", permission: "orders.read", group: "Orders", icon: ClipboardList, built: true },
  { href: "/orders/delivery-zones", label: "Delivery zones", permission: "config.administer", group: "Orders", icon: MapPin, built: true },
  // Order-flow behaviour chosen here instead of in config (2026-10-14). orders.administer, the permission
  // that settles a short order — so Sales / Account Manager as well as Super Admin (owner decision).
  { href: "/orders/settings", label: "Order settings", permission: "orders.administer", group: "Orders", icon: Gear, built: true },
  { href: "/dispatch", label: "Dispatch", permission: "dispatch.assign", group: "Orders", icon: Truck, built: true },
  // 008-reporting-dashboards T037: contracts/screen-routes.md's own permission for this route,
  // replacing the "nearest existing domain code" placeholder T020 used before T037 existed.
  { href: "/reports/order-to-invoice", label: "Order-to-invoice variance", permission: "orders.read", group: "Orders", icon: ScrollText, built: true },
  { href: "/reports/pipeline", label: "Order pipeline", permission: "orders.read", group: "Orders", icon: ChartBar, built: true },
  { href: "/inventory", label: "Stock", permission: "inventory.read", group: "Inventory", icon: Package, built: true },
  { href: "/inventory/exceptions", label: "Exceptions", permission: "inventory.read", group: "Inventory", icon: AlertTriangle, built: true },
  { href: "/inventory/receipts", label: "Goods receipt", permission: "inventory.administer", group: "Inventory", icon: Plus, built: true },
  { href: "/inventory/adjustments", label: "Adjustments", permission: "inventory.administer", group: "Inventory", icon: Sliders, built: true },
  { href: "/inventory/uploads", label: "Bulk upload", permission: "inventory.administer", group: "Inventory", icon: FileSpreadsheet, built: true },
  { href: "/reports/stock-movement", label: "Movement report", permission: "inventory.read", group: "Inventory", icon: ScrollText, built: true },
  // 008-reporting-dashboards T037: contracts/screen-routes.md's own permission for these two
  // routes, replacing the "nearest existing domain code" placeholder T015/T019 used before T037
  // existed. /reports/stock-movement above stays on `inventory.read` — 007's screen, owned elsewhere.
  // The §18 variance and weight-collision reports are the same computations as Packing's own
  // Variance/Collisions screens: gated here, listed once (under Packing) and on the Reports page.
  { href: "/reports/variance", label: "Variance report", permission: "packing.oversight", group: "Inventory", icon: AlertTriangle, built: true, menu: false },
  { href: "/reports/collisions", label: "Weight-collision report", permission: "packing.oversight", group: "Inventory", icon: AlertTriangle, built: true, menu: false },
  { href: "/customers", label: "Customers", permission: "customers.read", group: "Customers", icon: Users, built: true },
  { href: "/customers/trade-terms", label: "Trade-terms queue", permission: "customers.terms", group: "Customers", icon: Handshake, built: true },
  { href: "/customers/dedupe", label: "Duplicate records", permission: "customers.administer", group: "Customers", icon: Copy, built: true },
  { href: "/customers/newsletter", label: "Newsletter subscribers", permission: "customers.read", group: "Customers", icon: Envelope, built: true },
  { href: "/customers/ageing", label: "Ageing and statements", permission: "customers.credit", group: "Finance", icon: Wallet, built: true },
  { href: "/invoicing", label: "Invoicing", permission: "billing.read", group: "Invoicing", icon: Receipt, built: true },
  { href: "/invoicing/gst-summary", label: "GST summary", permission: "billing.read", group: "Invoicing", icon: FileSpreadsheet, built: true },
  // 008-reporting-dashboards T037: contracts/screen-routes.md's own permission, replacing the
  // "nearest existing domain code" placeholder T023 used before T037 existed.
  { href: "/reports/sales", label: "Sales report", permission: "billing.read", group: "Invoicing", icon: ChartBar, built: true },
  { href: "/invoicing/credit-notes", label: "Credit notes", permission: "billing.creditnote", group: "Invoicing", icon: FileMinus, built: true },
  // Read is `billing.read`, exactly as the API's GET /admin/invoice-series; opening or changing a series stays
  // `billing.administer` (Super Admin) inside the screen. It was gated on administer, so Accounts — who hold the
  // read and are the ones who have to explain a numbering gap — was refused a screen the list links them to.
  { href: "/invoicing/series", label: "Invoice series", permission: "billing.read", group: "Invoicing", icon: Hash, built: true },
  { href: "/finance/payments", label: "Payments", permission: "billing.read", group: "Finance", icon: CreditCard, built: true },
  { href: "/finance/transactions", label: "Gateway transactions", permission: "billing.read", group: "Finance", icon: CreditCard, built: true },
  { href: "/finance/refunds", label: "Refunds", permission: "billing.read", group: "Finance", icon: Undo2, built: true },
  // §17 Finance: receipts from Tally — Customers' ledger, so the Customers route's own `customers.credit`.
  { href: "/finance/receipts", label: "Receipts (from Tally)", permission: "customers.credit", group: "Finance", icon: Wallet, built: true },
  { href: "/integrations/tally", label: "Tally", permission: "integrations.read", group: "Integrations", icon: Plug, built: true },
  // §17 Integrations: the error log (Tally + IRN/e-way failures) and the gateway/GSP configuration.
  { href: "/integrations/errors", label: "Error log", permission: "integrations.read", group: "Integrations", icon: WarningOctagon, built: true },
  { href: "/integrations/settings", label: "Gateway & GSP", permission: "integrations.read", group: "Integrations", icon: Sliders, built: true },
  { href: "/settings/applicability", label: "Applicability", permission: "billing.administer", group: "Compliance", icon: ScrollText, built: true },
  // 005-packing-verification, admin-packing-oversight bucket (T053-T058, §17). All six gated on
  // `packing.oversight` except Tolerance rules — Super Admin only, per OversightController.cs's own
  // `[Authorize("packing.rules.administer")]`.
  { href: "/packing", label: "Live status", permission: "packing.oversight", group: "Packing", icon: Gauge, built: true },
  { href: "/packing/variance", label: "Variance", permission: "packing.oversight", group: "Packing", icon: AlertTriangle, built: true },
  { href: "/packing/overrides", label: "Overrides", permission: "packing.oversight", group: "Packing", icon: UserSwitch, built: true },
  { href: "/packing/spot-checks", label: "Spot-checks", permission: "packing.oversight", group: "Packing", icon: ListChecks, built: true },
  { href: "/packing/collisions", label: "Collisions", permission: "packing.oversight", group: "Packing", icon: WarningOctagon, built: true },
  { href: "/packing/tolerance-rules", label: "Tolerance rules", permission: "packing.rules.administer", group: "Packing", icon: Scales, built: true },
  // Identity's staff/account-administration surface — see types/index.ts's own note on these four
  // permission codes, each verified against its controller's own [Authorize] attribute.
  { href: "/users", label: "Users", permission: "user.status", group: "Administration", icon: UserGear, built: true },
  { href: "/invitations", label: "Invitations", permission: "company.user.invite", group: "Administration", icon: UserPlus, built: true },
  { href: "/audit", label: "Audit log", permission: "identity.audit.read", group: "Administration", icon: History, built: true },
  { href: "/role-matrix", label: "Role matrix", permission: "identity.matrix.administer", group: "Administration", icon: Key, built: true },
  // The mobile apps' force-update policies — platform configuration, as delivery zones are.
  { href: "/app-versions", label: "App versions", permission: "config.administer", group: "Administration", icon: DeviceMobile, built: true },
  // The customer home page's banners. catalogue.administer for reads too, as the API's own routes:
  // a banner scheduled for next week is not something a catalogue.read customer role should see.
  { href: "/website/banners", label: "Homepage banners", permission: "catalogue.administer", group: "Website", icon: Images, built: true },
  // Announcements in the customer mobile app — platform configuration, as app versions are.
  { href: "/global-messages", label: "Global messages", permission: "config.administer", group: "Website", icon: Megaphone, built: true },
  // The text pages (the privacy policy) the portal, the public website and the apps show —
  // config.administer, as global messages are. Each page's editor nests under /content/pages.
  { href: "/content/pages", label: "Pages", permission: "config.administer", group: "Content", icon: FileText, built: true },
];

/** Longest matching href wins — /tiers/new is gated by its own entry, not /tiers's. */
export function permissionForPath(pathname: string): Permission | undefined {
  return NAV.filter(
    (entry) => entry.href === pathname || (entry.href !== "/" && !entry.exact && pathname.startsWith(`${entry.href}/`)),
  ).sort((a, b) => b.href.length - a.href.length)[0]?.permission;
}

/** No `permission` on an entry means every signed-in account can reach it — see NavEntry's own note. */
function grantedFor(entry: NavEntry, permissions: ReadonlySet<Permission>): boolean {
  return entry.permission === undefined || permissions.has(entry.permission);
}

export function visibleNav(permissions: ReadonlySet<Permission>): readonly NavEntry[] {
  return NAV.filter((entry) => entry.built && entry.menu !== false && grantedFor(entry, permissions));
}

export function pendingNav(permissions: ReadonlySet<Permission>): readonly NavEntry[] {
  return NAV.filter((entry) => !entry.built && grantedFor(entry, permissions));
}

export function groupedNav(
  permissions: ReadonlySet<Permission>,
): readonly { group: NavGroup; entries: readonly NavEntry[] }[] {
  const visible = visibleNav(permissions);
  return NAV_GROUPS.map((group) => ({
    group,
    entries: visible.filter((entry) => entry.group === group),
  })).filter(({ entries }) => entries.length > 0);
}

/**
 * The nav in the shape `packages/theme`'s copied `sidebar-menu.tsx` walks: an entry with no `href`
 * is a group heading, an entry with one is a link. This is the "menu-items array replaced by our
 * routes" the shell copy calls for — data, built from `groupedNav`'s permission filter, so the
 * sidebar still shows only what the live matrix permits.
 */
export function menuItemsFor(permissions: ReadonlySet<Permission>): MenuItem[] {
  return groupedNav(permissions).flatMap(({ group, entries }) => [
    { name: group },
    ...entries.map((entry) => ({
      name: entry.label,
      href: entry.href,
      icon: <entry.icon aria-hidden />,
    })),
  ]);
}
